import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../utils/api';

interface TurnstileConfig {
  enabled: boolean;
  provider: string;
  siteKey: string;
  enabledRoutes: string[];
}

interface TurnstileWidgetProps {
  route: string;
  onToken: (token: string | null) => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, any>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const failed = () => { clearTimeout(timer); script.remove(); scriptPromise = null; reject(new Error('Botprüfung konnte nicht geladen werden.')); };
    const timer = window.setTimeout(failed, 15000);
    window.onTurnstileLoad = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = failed;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Cloudflare Turnstile widget.
 * Only renders if the integration is enabled and the current route is protected.
 * Calls onToken(token) when verification succeeds, onToken(null) on expiry.
 */
export default function TurnstileWidget({ route, onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [config, setConfig] = useState<TurnstileConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Fetch config once
  useEffect(() => {
    setError(false); setLoading(true); setConfig(null);
    api.get('/auth/bot-protection-config')
      .then(res => {
        const cfg = res.data?.config;
        if (cfg?.enabled && cfg.siteKey) {
          // Check if this route is protected
          if (cfg.enabledRoutes.length === 0 || cfg.enabledRoutes.includes(route)) {
            setConfig(cfg);
          }
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [route, attempt]);

  const handleToken = useCallback((token: string) => {
    onToken(token);
  }, [onToken]);

  const handleExpiry = useCallback(() => {
    onToken(null);
  }, [onToken]);

  // Render widget when config is available
  useEffect(() => {
    if (loading || !config || !containerRef.current) return;

    let mounted = true;

    loadTurnstileScript().then(() => {
      if (!mounted || !containerRef.current || !window.turnstile) return;

      // Clean up previous widget
      if (widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: config.siteKey,
        callback: handleToken,
        'expired-callback': handleExpiry,
        'error-callback': () => { handleExpiry(); setError(true); },
        theme: 'light',
        size: 'normal',
        language: 'de',
      });
    }).catch(() => { if (mounted) { handleExpiry(); setError(true); } });

    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [config, handleToken, handleExpiry, attempt, loading]);

  // Not enabled or not for this route
  if (error) return <div role="alert" className="my-3 text-sm text-red-700">Botprüfung nicht verfügbar. <button type="button" className="underline" onClick={() => { onToken(null); setError(false); setAttempt(value => value + 1); }}>Erneut versuchen</button></div>;
  if (loading || !config) return null;

  return (
    <div className="flex justify-center my-3">
      <div ref={containerRef} />
    </div>
  );
}
